import React from 'react';
import styled from 'styled-components';

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { RTC } from './useRTC';
import { Flags } from '@svg/../flags';
import { Alert, alpha, Chip, Divider, IconButton, Stack } from '@mui/material';
import { useTheme } from '../../../../../../styles';
import { Button } from '@common/Button';
import { Tooltip } from '@common/Tooltip';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import {
  MicOffOutlined,
  MicOutlined,
  PauseCircleOutline,
  PlayCircleOutline,
  VolumeOffOutlined,
  VolumeUpOutlined,
} from '@mui/icons-material';

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;

const CallHeader = styled.div`
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 3rem);
  padding: 0 1.5rem;
  padding-top: 0.25rem;

  .info {
    display: flex;
    align-items: flex-end;
    gap: 0.6rem;

    h1 {
      font-size: 16px;
      color: ${({ theme }) => theme.text.standard};
    }

    img {
      max-height: 21px;
    }
  }

  svg {
    font-size: 18px;
  }

  & .MuiIconButton-root {
    border: 1px solid ${({ theme }) => theme.line.light};
    border-radius: 8px;
  }

  & .MuiIconButton-root:hover {
    border-color: ${({ theme }) => alpha(theme.primary, 0.16)};

    svg {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

const CallActions = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: calc(100% - 3rem);
  border-bottom: 1px solid ${({ theme }) => theme.line.light};
  padding: 0 1.5rem;
  gap: 0.5rem;
  height: 40px;
`;

const CallNotice = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.line.light};
`;

interface Props {
  rtc: RTC;
  seconds: number;
  minutes: number;
}

export const SoftphoneState: React.FC<Props> = ({ rtc, seconds, minutes }) => {
  const theme = useTheme();

  const { isInitiating, isRinging, isAnswered, isHungup, isHeld, isMuted, isDeafened } = rtc.state;

  const call = rtc.call;
  const inProgress = Boolean(call);
  const run = useSelector((state: State) => state.tabs.active.run);
  const traces = useSelector((state: State) => state.tabs.active.run.traces) || [];
  const allNodesFinished = traces?.every((trace) => trace.metadata?.status !== 'processing');
  const contactNumber = rtc.state.contactNumber || '';
  const isActionAvailable = Boolean(isAnswered || isHeld);

  const phoneNumber = parsePhoneNumberFromString(contactNumber.replace(/ /g, ''));
  const valid = phoneNumber?.isValid();
  const flag = valid ? phoneNumber?.country?.toString() || 'UKN' : 'UKN';
  const formattedNumber = valid ? phoneNumber?.formatInternational() : contactNumber;

  const errorColor = `${theme.telnyx.error} !important`;

  const hangupCall = () => {
    if (!call) return;

    try {
      if (call.hangup) call.hangup();
    } catch (error) {
      console.error('Failed to terminate call', error);
    }
  };

  const toggleHold = () => {
    if (!call) return;

    if (isHeld) {
      try {
        if (call.unhold) call.unhold();
      } catch (error) {
        console.error('Failed to terminate call', error);
      }
    } else {
      try {
        if (call.hold) call.hold();
      } catch (error) {
        console.error('Failed to terminate call', error);
      }
    }
  };

  const toggleMute = () => {
    if (!call) return;

    if (isMuted) {
      try {
        if (call.unmuteAudio) {
          call.unmuteAudio();
          rtc.setMuted(false);
        }
      } catch (error) {
        console.error('Failed to terminate call', error);
      }
    } else {
      try {
        if (call.muteAudio) {
          call.muteAudio();
          rtc.setMuted(true);
        }
      } catch (error) {
        console.error('Failed to terminate call', error);
      }
    }
  };

  const toggleDeafen = () => {
    if (!call || !call.muteAudio) return;

    if (isDeafened) {
      try {
        if (call.undeaf) {
          call.undeaf();
          rtc.setDeafened(false);
        }
      } catch (error) {
        console.error('Failed to terminate call', error);
      }
    } else {
      try {
        if (call.deaf) {
          call.deaf();
          rtc.setDeafened(true);
        }
      } catch (error) {
        console.error('Failed to terminate call', error);
      }
    }
  };

  if (!inProgress) {
    return (
      <Alert severity='info' sx={{ m: 2 }}>
        No calls are currently in progress.
      </Alert>
    );
  }

  return (
    <Container>
      <CallHeader>
        <div className='info'>
          <Flags flag={flag} />
          <h1>{formattedNumber}</h1>
        </div>
        <Stack spacing={1} direction='row'>
          {/* <Tooltip title='Dialpad' placement='top' arrow>
            <IconButton color='primary' size='small' onClick={() => console.log('show dialpad')}>
              <DialpadOutlined />
            </IconButton>
          </Tooltip> */}
          <Tooltip title={isHeld ? 'Unhold' : 'Hold'} placement='top' arrow>
            <IconButton
              color={isHeld ? 'error' : 'primary'}
              size='small'
              onClick={toggleHold}
              disabled={!isActionAvailable}
            >
              {isHeld ? <PlayCircleOutline sx={{ color: errorColor }} /> : <PauseCircleOutline />}
            </IconButton>
          </Tooltip>
          <Tooltip title={isMuted ? 'Unmute' : 'Mute'} placement='top' arrow>
            <IconButton
              color={isMuted ? 'error' : 'primary'}
              size='small'
              onClick={toggleMute}
              disabled={!isActionAvailable}
            >
              {isMuted ? <MicOffOutlined sx={{ color: errorColor }} /> : <MicOutlined />}
            </IconButton>
          </Tooltip>
          <Tooltip title={isDeafened ? 'Undeafen' : 'Deafen'} placement='top' arrow>
            <IconButton
              color={isDeafened ? 'error' : 'primary'}
              size='small'
              onClick={toggleDeafen}
              disabled={!isActionAvailable}
            >
              {isDeafened ? <VolumeOffOutlined sx={{ color: errorColor }} /> : <VolumeUpOutlined />}
            </IconButton>
          </Tooltip>
        </Stack>
      </CallHeader>
      <CallActions>
        <Stack spacing={1} direction='row'>
          <Chip
            variant='outlined'
            size='small'
            sx={{ minWidth: '60px' }}
            label={`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}
          />
          <Divider orientation='vertical' flexItem />
          {isInitiating && <Chip variant='outlined' label='INITIATING' size='small' />}
          {isHeld && <Chip color='warning' variant='outlined' label='HELD' size='small' />}
          {isAnswered && <Chip color='primary' variant='outlined' label='ANSWERED' size='small' />}
          {isRinging && <Chip variant='outlined' color='success' size='small' label='RINGING' />}
          {isHungup && <Chip color='error' variant='outlined' size='small' label='TERMINATED' />}
        </Stack>
        <Button onClick={hangupCall} pill customColor='red' small disabled={!isActionAvailable}>
          Hangup
        </Button>
      </CallActions>
      {run?.running && isHungup && !allNodesFinished && (
        <CallNotice>
          <Alert severity='info'>
            {`The parent call has been terminated. The workflow will end when all remaining nodes are finished running. You can click "Stop Workflow" to prematurely end it.`}
          </Alert>
        </CallNotice>
      )}
    </Container>
  );
};
